import { Link, useNavigate } from 'react-router-dom';
import type { ProjectSummary } from '@spi/shared';
import { StatusBadge } from '../shared/StatusBadge';
import { OrgIcon, AskIcon } from '../shared/EntityIcons';

function formatQty(kg: number | null, packets: number | null, fulfilledKg: number, fulfilledPackets: number, isOpen: boolean): string {
  const parts: string[] = [];
  if (kg && kg > 0) {
    const remaining = kg - fulfilledKg;
    if (isOpen && fulfilledKg > 0) {
      parts.push(`${remaining} kg still needed`);
    } else {
      parts.push(`${kg} kg`);
    }
  }
  if (packets && packets > 0) {
    const remaining = packets - fulfilledPackets;
    if (isOpen && fulfilledPackets > 0) {
      parts.push(`${remaining} pkt still needed`);
    } else {
      parts.push(`${packets} pkt`);
    }
  }
  return parts.join(' + ') || '';
}

export function ProjectCard({ project }: { project: ProjectSummary }) {
  const navigate = useNavigate();
  const asks = project.askSummaries || [];

  return (
    <Link to={`/marketplace/projects/${project.id}`} className="card overflow-hidden hover:shadow-lg transition-shadow block">
      <div className="h-40 bg-spi-seed flex items-center justify-center overflow-hidden">
        {project.thumbnailUrl ? (
          <img src={project.thumbnailUrl} alt={project.title} className="w-full h-full object-cover" />
        ) : (
          <svg className="w-12 h-12 text-spi-green/30" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2C9 2 6 5 6 10c0 4 3 8 6 12 3-4 6-8 6-12 0-5-3-8-6-8zm0 11c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z" />
          </svg>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-semibold text-spi-charcoal text-base line-clamp-1">{project.title}</h3>
          <StatusBadge status={project.status} />
        </div>
        <p className="text-sm text-spi-slate mb-2">
          <span
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/marketplace/organizations/${project.orgId}`); }}
            className="inline-flex items-center gap-1 hover:text-spi-green cursor-pointer"
          >
            <OrgIcon className="w-3.5 h-3.5" />
            {project.orgName}
          </span>
        </p>
        <p className="text-xs text-spi-slate line-clamp-2 mb-3">{project.description}</p>

        {/* Seed requests */}
        {asks.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {asks.slice(0, 4).map((ask, i) => {
              const isOpen = ask.status === 'open' || ask.status === 'partially_fulfilled';
              const qty = formatQty(ask.quantityKg, ask.quantityPackets, ask.fulfilledKg, ask.fulfilledPackets, isOpen);
              return (
                <span
                  key={i}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${
                    isOpen
                      ? 'bg-spi-green/10 text-spi-green'
                      : 'bg-spi-parchment text-spi-slate line-through'
                  }`}
                >
                  <AskIcon className="w-3 h-3" />
                  {ask.crop}{qty ? ` (${qty})` : ''}
                </span>
              );
            })}
            {asks.length > 4 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-spi-seed text-spi-slate">
                +{asks.length - 4} more
              </span>
            )}
          </div>
        )}

        <div className="flex items-center justify-between text-xs text-spi-slate">
          <span>{project.country}{project.zone ? ` / Zone ${project.zone}` : ''}</span>
          <span className="inline-flex items-center gap-1">
            <AskIcon className="w-3 h-3" />
            {project.openAskCount} of {project.askCount} asks open
          </span>
        </div>
      </div>
    </Link>
  );
}
